import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Stack,
  Card,
  Button,
  Input,
  Textarea,
  Select,
  Checkbox,
  RadioGroup,
  Switch,
  Alert,
  Badge,
  Avatar,
  Modal,
  Divider,
  Link,
} from '@/components/ui';
import { useState } from 'react';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  message: Yup.string().required('Message is required'),
  country: Yup.string().required('Country is required'),
  plan: Yup.string().required('Please select a plan'),
  terms: Yup.boolean().oneOf([true], 'You must accept the terms'),
  notifications: Yup.boolean(),
});

export default function ComponentShowcasePage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alertVisible, setAlertVisible] = useState(true);

  return (
    <Container size="lg">
      <div className="space-y-12 py-12">
        {/* Header */}
        <div>
          <h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-white">
            Component Showcase
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400">
            A comprehensive demonstration of all reusable UI components in the design system.
          </p>
        </div>

        {/* Alerts */}
        <Card title="Alerts" subtitle="Display important messages">
          <Stack spacing={4}>
            {alertVisible && (
              <Alert variant="info" title="Information" onClose={() => setAlertVisible(false)}>
                This is an informational alert message.
              </Alert>
            )}
            <Alert variant="success" title="Success">
              Your changes have been saved successfully.
            </Alert>
            <Alert variant="warning" title="Warning">
              Your session will expire in 5 minutes.
            </Alert>
            <Alert variant="error" title="Error">
              There was an error processing your request.
            </Alert>
          </Stack>
        </Card>

        {/* Buttons */}
        <Card title="Buttons" subtitle="Action buttons with different variants and sizes">
          <Stack spacing={6}>
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Variants
              </h3>
              <Stack direction="horizontal" spacing={3}>
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger">Danger</Button>
              </Stack>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Sizes</h3>
              <Stack direction="horizontal" spacing={3} align="center">
                <Button variant="primary" size="sm">
                  Small
                </Button>
                <Button variant="primary" size="md">
                  Medium
                </Button>
                <Button variant="primary" size="lg">
                  Large
                </Button>
              </Stack>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                States
              </h3>
              <Stack direction="horizontal" spacing={3}>
                <Button variant="primary" loading>
                  Loading
                </Button>
                <Button variant="primary" disabled>
                  Disabled
                </Button>
                <Button variant="primary" fullWidth>
                  Full Width
                </Button>
              </Stack>
            </div>
          </Stack>
        </Card>

        {/* Badges */}
        <Card title="Badges" subtitle="Status indicators and labels">
          <Stack direction="horizontal" spacing={3}>
            <Badge variant="default">Default</Badge>
            <Badge variant="primary">Primary</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="error">Error</Badge>
          </Stack>
        </Card>

        {/* Avatars */}
        <Card title="Avatars" subtitle="User profile pictures with status">
          <Stack direction="horizontal" spacing={4} align="center">
            <Avatar alt="John Doe" fallback="JD" size="xs" />
            <Avatar alt="Jane Smith" fallback="JS" size="sm" status="online" />
            <Avatar alt="Bob Johnson" fallback="BJ" size="md" status="away" />
            <Avatar alt="Alice Williams" fallback="AW" size="lg" status="busy" />
            <Avatar alt="Charlie Brown" fallback="CB" size="xl" status="offline" />
          </Stack>
        </Card>

        {/* Form Example */}
        <Card title="Forms" subtitle="Input fields with Formik integration">
          <Formik
            initialValues={{
              name: '',
              email: '',
              message: '',
              country: '',
              plan: '',
              terms: false,
              notifications: false,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                console.log('Form values:', values);
                alert('Form submitted! Check console for values.');
                setSubmitting(false);
              }, 1000);
            }}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-6">
                <Stack spacing={4}>
                  <Input name="name" label="Full Name" placeholder="John Doe" required />

                  <Input
                    name="email"
                    type="email"
                    label="Email Address"
                    placeholder="john@example.com"
                    required
                  />

                  <Select
                    name="country"
                    label="Country"
                    placeholder="Select a country"
                    options={[
                      { value: 'us', label: 'United States' },
                      { value: 'uk', label: 'United Kingdom' },
                      { value: 'ca', label: 'Canada' },
                      { value: 'au', label: 'Australia' },
                    ]}
                    required
                  />

                  <RadioGroup
                    name="plan"
                    label="Select a Plan"
                    options={[
                      { value: 'free', label: 'Free', description: 'Basic features' },
                      { value: 'pro', label: 'Pro', description: 'Advanced features' },
                      { value: 'enterprise', label: 'Enterprise', description: 'All features' },
                    ]}
                  />

                  <Textarea
                    name="message"
                    label="Message"
                    placeholder="Enter your message..."
                    rows={4}
                    required
                  />

                  <Checkbox
                    name="terms"
                    label="I agree to the terms and conditions"
                    description="You must agree before submitting"
                  />

                  <Switch
                    name="notifications"
                    label="Enable email notifications"
                    description="Receive updates about your account"
                  />
                </Stack>

                <Divider />

                <Stack direction="horizontal" spacing={3} justify="end">
                  <Button type="button" variant="ghost">
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" loading={isSubmitting}>
                    Submit Form
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Card>

        {/* Modal */}
        <Card title="Modal" subtitle="Dialog overlays">
          <Button variant="primary" onClick={() => setIsModalOpen(true)}>
            Open Modal
          </Button>

          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Modal Example"
            footer={
              <>
                <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => setIsModalOpen(false)}>
                  Confirm
                </Button>
              </>
            }
          >
            <p className="text-gray-500 dark:text-gray-400">
              This is a modal dialog. It can contain any content you need.
            </p>
          </Modal>
        </Card>

        {/* Links */}
        <Card title="Links" subtitle="Navigation links">
          <Stack spacing={2}>
            <div>
              <Link href="/dashboard">Internal Link</Link>
            </div>
            <div>
              <Link href="https://example.com" external>
                External Link
              </Link>
            </div>
          </Stack>
        </Card>

        {/* Layout Components */}
        <Card title="Layout" subtitle="Container and Stack components">
          <Stack spacing={4}>
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Vertical Stack
              </h3>
              <Stack spacing={2}>
                <div className="rounded bg-primary-100 p-4 dark:bg-primary-900/30">Item 1</div>
                <div className="rounded bg-primary-100 p-4 dark:bg-primary-900/30">Item 2</div>
                <div className="rounded bg-primary-100 p-4 dark:bg-primary-900/30">Item 3</div>
              </Stack>
            </div>

            <Divider />

            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Horizontal Stack
              </h3>
              <Stack direction="horizontal" spacing={2}>
                <div className="rounded bg-primary-100 p-4 dark:bg-primary-900/30">Item 1</div>
                <div className="rounded bg-primary-100 p-4 dark:bg-primary-900/30">Item 2</div>
                <div className="rounded bg-primary-100 p-4 dark:bg-primary-900/30">Item 3</div>
              </Stack>
            </div>
          </Stack>
        </Card>
      </div>
    </Container>
  );
}
